define('utils/util', ['jquery', 'Promise'], function ($, Promise) {
	'use strict';

	var setTimeout = window.setTimeout,
		setImmediate = window.setImmediate || setTimeout;

	/**
	 * @typedef  {Function} DebouncedFunction
	 * @property {number} pid
	 * @property {Function} cancel
	 */


	/**
	 * Базовые утилиты
	 * @module util
	 */
	var util = {

		/**
		 * Объединяет содержимое двух или более объектов.
		 * @memberOf util
		 * @param  {...Object} args
		 * @returns {Object}
		 */
		extend: function (args) {
			return $.extend.apply($, arguments);
		},


		/**
		 * Клонирование через JSON
		 * @param   {*} src
		 * @returns {*}
		 */
		cloneJSON: function (src) {
			return typeof src === 'object' ? JSON.parse(JSON.stringify(src)) : src;
		},


		/**
		 * Быстрое клонирование объекта
		 * @memberOf util
		 * @param   {Object}  obj
		 * @returns {Object}
		 */
		cloneObject: function (obj) {
			var ret = {}, key;

			if (obj && obj instanceof Object) {
				for (key in obj) {
					ret[key] = obj[key];
				}
			}

			return ret;
		},


		/**
		 * Метод для приведения строк к camelCase нотации
		 * @memberOf util
		 * @param  {string} str          Строка в snake нотации
		 * @param  {string} [delimiter]  Разделитель, по умолчанию '_'
		 * @return {string}              Строка в camelCase
		 */
		toCamelCase: function (str, delimiter) {
			delimiter = delimiter || '_';

			var regexp = new RegExp('(\\' + delimiter + '\\w)', 'g');

			return str.replace(regexp, function (match) {
				return match[1].toUpperCase();
			});
		},


		/**
		 * Выполнить функцию в следующем «тике«
		 * @param   {Function} fn
		 * @param   {boolean}  [returnFunc]
		 * @returns {undefined|Function}
		 */
		nextTick: function (fn, returnFunc) {
			if (returnFunc) {
				return function () {
					var args = arguments,
						_this = this;

					setImmediate(function () {
						fn.apply(_this, args);
					});
				};
			}
			else {
				setImmediate(fn);
			}
		},


		/**
		 * Отложить выполнение метода
		 * @param  {Function} fn
		 * @param  {number} [ms] минимальная задаржка перед выполнением
		 * @param  {Object} [thisArg]
		 * @return {DebouncedFunction}
		 */
		debounce: function (fn, ms, thisArg) {
			var debounced = function () {
				var args = arguments,
					length = args.length,
					ctx = thisArg || this;

				clearTimeout(debounced.pid);
				debounced.pid = setTimeout(function () {
					if (length === 0) {
						fn.call(ctx);
					}
					else if (length === 1) {
						fn.call(ctx, args[0]);
					}
					else if (length === 2) {
						fn.call(ctx, args[0], args[1]);
					}
					else if (length === 3) {
						fn.call(ctx, args[0], args[1], args[2]);
					}
					else {
						fn.apply(ctx, args);
					}
				}, ms);
			};

			debounced.cancel = _cancelDebounce;

			return debounced;
		},


		/**
		 * Быстрый `bind`
		 * @param  {*}         thisArg
		 * @param  {Function}  fn
		 * @param  {Array|null}     [args]
		 * @param  {Array}     [limit]
		 */
		bind: function (thisArg, fn, args, limit) {
			/* jshint eqnull:true */
			if (args == null) {
				args = [];
			}
			else if (!Array.isArray(args)) {
				throw 'util.bind: `args` must be array or null';
			}

			return function () {
				var _args = args;
				var _limit = limit;

				/* jshint eqnull:true */
				if (_limit == null) {
					_limit = arguments.length;
				}

				/* istanbul ignore else */
				if (_limit > 0) {
					_args = args.slice();

					for (var i = 0; i < _limit; i++) {
						_args.push(arguments[i]);
					}
				}

				return fn.apply(thisArg || this, _args);
			};
		},

		/**
		 * Тот же `bind`, но не учутываются аргументы при вызове
		 * @param  {Function}  fn
		 * @param  {Array}     [args]
		 * @param  {*}         [thisArg]
		 */
		bindWithoutArgs: function (fn, args, thisArg) {
			return Array.isArray(args)
				? function () { return fn.apply(thisArg || this, args); }
				: function () { return fn.call(thisArg || this); }
			;
		},


		/**
		 * Создать массив элементов
		 * @param {number} length
		 * @returns {number[]}
		 */
		range: function (length) {
			var array = [],
				i = 0;

			for (; i < length; i++) {
				array.push(i);
			}

			return array;
		},

		/**
		 * Получить свойства объектов в коллекции
		 * @param {Array}  collection
		 * @param {string} property 
		 * @returns {Array}
		 */
		pluck: function (collection, property) {
			if (!Array.isArray(collection)) {
				return [];
			}

			return collection.map(function (obj) {
				return obj[property];
			});
		},

		/**
		 * Создать объект
		 * @param {Array|Array[]} keys
		 * @param {Array}         [values]
		 * @returns {Object}
		 */
		object: function (keys, values) {

			keys = (keys === void(0) || keys === null) ? [] : [].concat(keys);
			values = values === void(0) ? [] : [].concat(values);

			return keys.reduce(function (obj, key, index) {
				if (Array.isArray(key)) {
					obj[key[0]] = key[1];

					return obj;
				}

				obj[key] = values[index];

				return obj;
			}, {});
		},

		/**
		 * Промифицировать асинхронную функцию, которая возвращает значения через callback (err, res)
		 * @param {Function} fn - функция, которую нужно промифицировать
		 * @param {*} [context] - контекст вызова функции
		 * @returns {Function} - промфицированя функция
		 */
		promisify: function (fn, context) {
			return function promisifyWrapper() {
				var args = Array.prototype.slice.call(arguments);

				if (!context) {
					context = this;
				}

				return new Promise(function promisifyWrapperCallback(resolve, reject) {

					args.push(function (err, res) {
						if (err) {
							reject(err);
						} else {
							resolve(res);
						}
					});

					fn.apply(context, args);
				});
			};
		}
	};


	function _cancelDebounce() {
		/* jshint validthis:true */
		clearTimeout(this.pid);
	}


	// Export
	util.version = '0.7.0';
	return util;
});
